// WebSocketInitializer.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { webSocketService } from './WebSocketService';
import { FeedActionContext, useFeedActions } from './FeedState';
import { useSubscribedSymbolsContext } from './context/DataFeedSymbolsContext';
import { useDispatcher } from '../global/hooks';
import { useFeedStatus, useWebSocketCloseReason } from './FeedSelectors';
import { useStateSelector } from '../global/dataSelectors';
import { createSelector } from 'reselect';
import { DataState } from '../global/state';
import { Websockets } from '../../constants';
import _ from 'lodash';

const canConnectToWebsocket = createSelector(
  (s: DataState) => s.auth,
  auth => (auth.isExpired !== true && auth.isAuthenticated === true ? true : false)
);

const WebSocketInitializer: React.FC = () => {
  const dispatch = useContext(FeedActionContext);
  const [, setSubscribedQuotes] = useSubscribedSymbolsContext();
  const [shouldReconnect, setShouldReconnect] = useState(false);
  const retryTimeoutMs = useRef<number>(250);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const dispatchers = useDispatcher();
  const dispatcher = dispatchers.dispatcher.feed;
  const globalDispatch = dispatchers.dispatch;
  const feedStatus = useFeedStatus();
  const closeReason = useWebSocketCloseReason();
  const canConnect = useStateSelector(canConnectToWebsocket);

  const onClose = useCallback(() => {
    setSubscribedQuotes([]);
  }, [setSubscribedQuotes]);

  useEffect(() => {
    return () => {
      // Clean up WebSocket on unmount if needed
      onClose();
      webSocketService.closeConnection();
    };
  }, []);

  useEffect(() => {
    if (shouldReconnect) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        if (webSocketService.shouldTryReconnect()) {
          onClose();
          webSocketService.connect(dispatch, dispatcher, globalDispatch).then(() => setShouldReconnect(false));
        }
      }, retryTimeoutMs.current);
    }
  }, [shouldReconnect]);

  useEffect(() => {
    if (feedStatus === 'closed') {
      if (canConnect) {
        const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
        const newTimeoutDisplay = _.round(newTimeout / 1000, 2);
        const closeMessage = `Socket is closed. Reconnect will be attempted in ${newTimeoutDisplay} second.`;
        dispatcher.closed({
          reason: closeReason || '',
          retryingInSeconds: newTimeoutDisplay,
          message: closeMessage,
        });
        retryTimeoutMs.current = newTimeout;
        console.warn(closeMessage);
        setShouldReconnect(true);
      } else {
        const closeMessage = 'Socket is closed because user can no longer connect';
        dispatcher.closed({ reason: closeReason || '', message: closeMessage });
        console.warn(closeMessage);
      }
    }
  }, [feedStatus, closeReason, canConnect]);

  useEffect(() => {
    if (canConnect) {
      console.log('initializing websocket (this should only happen once!)');
      webSocketService.connect(dispatch, dispatcher, globalDispatch);
    } else if (!canConnect && !webSocketService.shouldTryReconnect()) {
      console.log('disconnecting websocket because user can no longer connect');
      onClose();
      webSocketService.closeConnection();
    } else {
      console.log('doing nothing');
    }
  }, [dispatcher, canConnect]);

  return null; // This component does not render anything
};

export default WebSocketInitializer;
